import { createTheme, Theme } from '@mui/material/styles'

// The few MUI pieces the site uses (Tooltip, Menu, Alert, Slider, Rating,
// CircularProgress, Skeleton) used to render in the stock theme: Roboto,
// #1976d2 and a 4px radius next to the site's Mulish, brand blue and 12px
// controls. One theme per colour theme (styles/index.css): MUI mixes its
// colours (alpha, darken), so it takes plain colours, not CSS variables.
const PALETTES = {
  light: {
    mode: 'light',
    primary: { main: '#3c5ef4', dark: '#2f4fe0', light: '#5370f6', contrastText: '#ffffff' },
    error: { main: '#b42318' },
    success: { main: '#0b6b41' },
    warning: { main: '#92400e' },
    text: { primary: '#212430', secondary: '#5b5f6b' },
    background: { default: '#f6f6fe', paper: '#ffffff' },
  },
  dark: {
    mode: 'dark',
    primary: { main: '#3c5ef4', dark: '#2f4fe0', light: '#7b93ff', contrastText: '#ffffff' },
    error: { main: '#ff8a80' },
    success: { main: '#5fd49a' },
    warning: { main: '#f5c26b' },
    text: { primary: '#e9ebf3', secondary: '#a6abba' },
    background: { default: '#0f1117', paper: '#1a1d26' },
  },
} as const

const makeTheme = (mode: 'light' | 'dark'): Theme =>
  createTheme({
    typography: {
      fontFamily: '"Mulish", "Inter", ui-sans-serif, system-ui, sans-serif',
      button: { textTransform: 'none', fontWeight: 700 },
    },
    palette: PALETTES[mode],
    shape: { borderRadius: 12 },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#212430',
            fontSize: '0.8125rem',
            lineHeight: 1.45,
            fontWeight: 600,
            borderRadius: 8,
            padding: '8px 10px',
            // on a dark page the dark tooltip gets a faint edge
            ...(mode === 'dark' && { boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.12)' }),
          },
          arrow: { color: '#212430' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 12, boxShadow: 'var(--shadow-pop)', backgroundImage: 'none' },
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { minHeight: 44, fontWeight: 600 } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
      // empty stars in the control colour: MUI's pale grey read at 1.8:1
      // (3:1 is the floor for a control's parts)
      MuiRating: {
        styleOverrides: { iconEmpty: { color: 'rgb(var(--control))' } },
      },
    },
  })

const muiThemes = { light: makeTheme('light'), dark: makeTheme('dark') }

export default muiThemes
